/**
 * AI Generation React Query Hooks (SongForge)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AIGenerationService } from '../api/services/aiGenerationService';
import type {
  AIReferenceSong,
  AIGenerationQueueItem,
  AIPromptTemplate,
  CreateAIReferenceSongRequest,
  CreateAIGenerationQueueRequest,
  CreateAIPromptTemplateRequest,
  AIGenerationStatus,
} from '../api/types';

// Query keys
export const aiGenerationKeys = {
  all: ['songforge'] as const,

  references: {
    all: ['ai-references'] as const,
    list: (params?: any) => [...aiGenerationKeys.references.all, 'list', params] as const,
    detail: (id: string) => [...aiGenerationKeys.references.all, id] as const,
  },

  queue: {
    all: ['ai-queue'] as const,
    list: (params?: any) => [...aiGenerationKeys.queue.all, 'list', params] as const,
    detail: (id: string) => [...aiGenerationKeys.queue.all, id] as const,
  },

  templates: {
    all: ['ai-templates'] as const,
    list: (params?: any) => [...aiGenerationKeys.templates.all, 'list', params] as const,
    detail: (id: string) => [...aiGenerationKeys.templates.all, id] as const,
  },

  coverage: (orgId?: string) => [...aiGenerationKeys.all, 'coverage', orgId] as const,
  stats: (orgId?: string) => [...aiGenerationKeys.all, 'stats', orgId] as const,
};

// =============================================================================
// Reference Song Hooks
// =============================================================================

export function useAIReferences(params?: {
  organization_id?: string;
  genre?: string;
  mood?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: aiGenerationKeys.references.list(params),
    queryFn: () => AIGenerationService.listReferences(params),
  });
}

export function useAIReference(id: string) {
  return useQuery({
    queryKey: aiGenerationKeys.references.detail(id),
    queryFn: () => AIGenerationService.getReference(id),
    enabled: !!id,
  });
}

export function useCreateAIReference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAIReferenceSongRequest) =>
      AIGenerationService.createReference(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.references.all });
    },
  });
}

export function useUpdateAIReference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AIReferenceSong> }) =>
      AIGenerationService.updateReference(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.references.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.references.detail(id) });
    },
  });
}

export function useDeleteAIReference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AIGenerationService.deleteReference(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.references.all });
    },
  });
}

// =============================================================================
// Generation Queue Hooks
// =============================================================================

export function useAIQueue(params?: {
  organization_id?: string;
  status?: AIGenerationStatus;
  target_genre?: string;
  target_mood?: string;
}) {
  return useQuery({
    queryKey: aiGenerationKeys.queue.list(params),
    queryFn: () => AIGenerationService.listQueue(params),
  });
}

export function useAIQueueItem(id: string) {
  return useQuery({
    queryKey: aiGenerationKeys.queue.detail(id),
    queryFn: () => AIGenerationService.getQueueItem(id),
    enabled: !!id,
  });
}

export function useCreateAIQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAIGenerationQueueRequest) =>
      AIGenerationService.createQueueItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.stats() });
    },
  });
}

export function useUpdateAIQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AIGenerationQueueItem> }) =>
      AIGenerationService.updateQueueItem(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.detail(id) });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.stats() });
    },
  });
}

export function useDeleteAIQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AIGenerationService.deleteQueueItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.stats() });
    },
  });
}

export function useLinkAIContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      queueItemId,
      contentId,
      variationLabel,
      setAsPrimary,
    }: {
      queueItemId: string;
      contentId: string;
      variationLabel?: string;
      setAsPrimary?: boolean;
    }) =>
      AIGenerationService.linkContent(queueItemId, contentId, {
        variation_label: variationLabel,
        set_as_primary: setAsPrimary,
      }),
    onSuccess: (_, { queueItemId }) => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.detail(queueItemId) });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.stats() });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.coverage() });
    },
  });
}

export function useUnlinkAIContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ queueItemId, contentId }: { queueItemId: string; contentId: string }) =>
      AIGenerationService.unlinkContent(queueItemId, contentId),
    onSuccess: (_, { queueItemId }) => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.detail(queueItemId) });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.stats() });
    },
  });
}

export function useSetPrimaryTrack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ queueItemId, contentId }: { queueItemId: string; contentId: string }) =>
      AIGenerationService.setPrimaryTrack(queueItemId, contentId),
    onSuccess: (_, { queueItemId }) => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.queue.detail(queueItemId) });
    },
  });
}

// =============================================================================
// Template Hooks
// =============================================================================

export function useAITemplates(params?: {
  organization_id?: string;
  genre?: string;
  mood?: string;
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: aiGenerationKeys.templates.list(params),
    queryFn: () => AIGenerationService.listTemplates(params),
  });
}

export function useAITemplate(id: string) {
  return useQuery({
    queryKey: aiGenerationKeys.templates.detail(id),
    queryFn: () => AIGenerationService.getTemplate(id),
    enabled: !!id,
  });
}

export function useCreateAITemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAIPromptTemplateRequest) =>
      AIGenerationService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.templates.all });
    },
  });
}

export function useUpdateAITemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AIPromptTemplate> }) =>
      AIGenerationService.updateTemplate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.templates.all });
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.templates.detail(id) });
    },
  });
}

export function useDeleteAITemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AIGenerationService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.templates.all });
    },
  });
}

export function useIncrementTemplateUsage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AIGenerationService.incrementTemplateUsage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiGenerationKeys.templates.all });
    },
  });
}

// =============================================================================
// Analytics Hooks
// =============================================================================

export function useAICoverage(organizationId?: string) {
  return useQuery({
    queryKey: aiGenerationKeys.coverage(organizationId),
    queryFn: () => AIGenerationService.getCoverage(organizationId),
    staleTime: 60000, // 1 minute
  });
}

export function useAIStats(organizationId?: string) {
  return useQuery({
    queryKey: aiGenerationKeys.stats(organizationId),
    queryFn: () => AIGenerationService.getStats(organizationId),
    staleTime: 60000, // 1 minute
  });
}
